import { FileText, Calendar, Loader2 } from "lucide-react";
import { useCandidateCertifications } from "@/pages/candidate-profile/hooks/useCandidateProfile";
import type { CertificationDetail } from "@/interface/certification";

export function CertificationsTab({
  profileId,
  certs = [],
}: {
  profileId?: number | string;
  certs?: CertificationDetail[];
}) {
  const {
    data: certsQuery,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useCandidateCertifications(profileId, { page: 1, page_size: 12 });

  const queryCerts = certsQuery?.pages?.flatMap((page: any) =>
    Array.isArray(page?.data) ? page.data : page?.data?.results || [],
  );

  const certificationList: CertificationDetail[] =
    queryCerts && queryCerts.length > 0
      ? queryCerts
      : Array.isArray(certs)
        ? certs
        : [];

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 50) {
      if (hasNextPage && !isFetchingNextPage) {
        fetchNextPage();
      }
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-6 shadow-sm flex flex-col max-h-[750px]">
      <h2 className="text-lg font-bold text-primary mb-6 border-b border-slate-100 dark:border-slate-800 pb-4 shrink-0">
        Professional Certifications
      </h2>

      {isLoading && certificationList.length === 0 ? (
        <div className="flex justify-center items-center p-12">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      ) : (
        <div
          onScroll={handleScroll}
          className="overflow-y-auto pr-1 flex-1 space-y-4"
        >
          {certificationList.length === 0 ? (
            <div className="text-center p-8 text-slate-500 text-sm">
              No certifications listed.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {certificationList.map((cert: CertificationDetail, i: number) => {
                const certName = cert.certification_name || "Certification";
                const issuer = cert.issuing_organization || "Organization N/A";
                const issueDate = cert.issue_date || "";

                return (
                  <div
                    key={cert.id || i}
                    className="flex items-start gap-4 p-5 border border-slate-100 dark:border-slate-800 rounded-xl hover:border-blue-500/50 transition-colors"
                  >
                    <div className="h-12 w-12 bg-blue-500/10 rounded-xl flex items-center justify-center shrink-0 border border-blue-500/20">
                      <FileText className="h-6 w-6 text-blue-500" />
                    </div>
                    <div>
                      <h4 className="font-bold text-sm text-slate-900 dark:text-white leading-tight">
                        {certName}
                      </h4>
                      <p className="text-xs text-slate-500 mt-1.5">
                        Issued by {issuer}
                      </p>
                      {issueDate && (
                        <p className="mt-1 text-xs font-medium text-slate-400 flex items-center gap-1">
                          <Calendar className="h-3 w-3" /> {issueDate}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {isFetchingNextPage && (
            <div className="flex justify-center items-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
              <span className="ml-2 text-xs text-slate-500 font-medium">
                Loading more certifications...
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
